import { Header } from "build-ts/metadata";
import React, { useEffect, useState } from "react";
import { useUser } from "context/UserContext";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { listScheduledJobs } from "services/ScheduleServices";
import { listJobArtifacts } from "services/AgentChatService";
import { AnalysisOutputProto, ArtifactProto } from "build-ts/chat_session";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { AppIcons } from "constants/icons";
import { ArtifactType, artifactTypeToJSON } from "build-ts/common";

interface AnalysisRunProps {
  analystsList: Header[];
  isTabActive: boolean;
}

type AnalysisOutputsViewMode = "withFindings" | "runsOnly";

function analysisRunTitle(output: AnalysisOutputProto, defaultTimeZone: string | undefined): string {
  const createdDate =
    output.header?.created_date && defaultTimeZone
      ? epochConverterWithTimeZone(output.header.created_date, "h:mm a, d MMM", defaultTimeZone)
      : undefined;
  return createdDate ? `Workflow Run at: ${createdDate}` : "Workflow Output";
}

interface RunListRowProps {
  output: AnalysisOutputProto;
  title: string;
  chatId: string | undefined;
}

const RunListRow: React.FC<RunListRowProps> = ({ title, output, chatId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const findings = output.findings?.trim() || "";

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      _hover={{ borderColor: "purple.300" }}
      transition="border-color 0.15s"
    >
      <Flex
        px={3}
        py={2.5}
        align="center"
        gap={3}
        cursor="pointer"
        bg={isExpanded ? "purple.50" : "white"}
        _hover={{ bg: "purple.50" }}
        transition="background 0.12s"
        onClick={() => setIsExpanded(p => !p)}
      >
        <Icon as={AppIcons.workflow} boxSize={5} color="purple.600" flexShrink={0} />
        <Box flex={1} minW={0} display="flex" justifyContent="flex-start" alignItems="center">
          <Text
            as="span"
            display="inline-block"
            maxW="100%"
            fontSize="md"
            fontWeight="600"
            color={chatId ? "purple.700" : "gray.800"}
            textAlign="left"
            noOfLines={2}
            m={0}
            mb={0}
            cursor={chatId ? "pointer" : "inherit"}
            _hover={chatId ? { textDecoration: "underline", color: "purple.800" } : undefined}
            onClick={e => {
              if (!chatId) return;
              e.stopPropagation();
              window.open(`/chat/${chatId}`, "_blank");
            }}
          >
            {title}
          </Text>
        </Box>
        <Icon as={isExpanded ? AppIcons.chevronUp : AppIcons.chevronDown} boxSize={3} color="gray.500" flexShrink={0} />
      </Flex>
      <Collapse in={isExpanded} animateOpacity>
        <Box px={4} py={3} borderTop="1px solid" borderColor="gray.100" fontSize="md">
          {findings ? (
            <MarkdownRenderer content={findings} />
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic" m={0} mb={0}>
              No findings recorded.
            </Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const AnalysisRun: React.FC<AnalysisRunProps> = ({ analystsList, isTabActive }) => {
  const { defaultAnalystId, defaultTimeZone } = useUser();

  const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<Header[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Header | null>(null);
  const [outputs, setOutputs] = useState<ArtifactProto[]>([]);
  const [totalOutputs, setTotalOutputs] = useState(0);
  const [isLoadingOutputs, setIsLoadingOutputs] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [outputsViewMode, setOutputsViewMode] = useState<AnalysisOutputsViewMode>("withFindings");

  // Auto-select default analyst on mount / when analystsList loads
  useEffect(() => {
    if (analystsList.length === 0) return;
    const defaultAnalyst = analystsList.find(a => a.id === defaultAnalystId) || analystsList[0];
    setSelectedAnalyst(defaultAnalyst);
  }, [defaultAnalystId, analystsList]);

  // Fetch scheduled jobs whenever the selected analyst changes
  useEffect(() => {
    if (!selectedAnalyst?.id || !isTabActive) return;
    const fetchJobs = async () => {
      setIsLoadingJobs(true);
      setScheduledJobs([]);
      setSelectedJob(null);
      setOutputs([]);
      const response = await listScheduledJobs(
        "SCENARIO_JOB",
        -1,
        1,
        selectedAnalyst.id,
        "LAST_MODIFIED",
        false,
        true,
        ""
      );
      setScheduledJobs(response || []);
      setIsLoadingJobs(false);
    };
    fetchJobs();
  }, [selectedAnalyst?.id, isTabActive]);

  // Fetch analysis outputs whenever the selected job changes
  useEffect(() => {
    if (!selectedJob?.id) {
      setOutputs([]);
      setTotalOutputs(0);
      return;
    }
    fetchOutputs(selectedJob.id, 0, false);
  }, [selectedJob?.id]);

  const fetchOutputs = async (jobId: string, offset: number, append: boolean) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingOutputs(true);
    }
    const { items, total } = await listJobArtifacts(
      artifactTypeToJSON(ArtifactType.ANALYSIS_OUTPUT),
      jobId,
      50,
      offset
    );
    setOutputs(prev => (append ? [...prev, ...items] : items));
    setTotalOutputs(total);
    if (append) {
      setIsLoadingMore(false);
    } else {
      setIsLoadingOutputs(false);
    }
  };

  const handleLoadMore = () => {
    if (selectedJob?.id) {
      fetchOutputs(selectedJob.id, outputs.length, true);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      {/* Analyst & Job selectors */}
      <Flex gap={3} align="flex-start">
        {/* Analyst */}
        <Box flex={1}>
          <Flex align="center" gap={2} mb={2}>
            <Icon as={AppIcons.analyst} boxSize={5} color="purple.700" />
            <Text fontSize="md" fontWeight="600" color="gray.600" m={0}>
              Analyst
            </Text>
          </Flex>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              colorScheme="purple"
              bg="purple.50"
              borderColor="purple.50"
              _hover={{ bg: "purple.100", borderColor: "purple.100" }}
              borderRadius="lg"
              borderWidth="1px"
              px={3}
              py={1}
              w="100%"
              justifyContent="flex-start"
            >
              <HStack spacing={2} justify="space-between" w="100%">
                <Text fontSize="sm" color="fg.default" fontWeight="600" m={0} noOfLines={1}>
                  {selectedAnalyst ? selectedAnalyst.name : "Select analyst"}
                </Text>
                <Icon as={AppIcons.chevronDown} boxSize={3} />
              </HStack>
            </MenuButton>
            <MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg" zIndex={1500}>
              {analystsList.map(analyst => (
                <MenuItem
                  key={analyst.id}
                  fontSize="sm"
                  bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
                  _hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
                  onClick={() => setSelectedAnalyst(analyst)}
                >
                  <HStack spacing={2}>
                    <Icon
                      as={AppIcons.analyst}
                      boxSize={5}
                      color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"}
                    />
                    <Text
                      fontSize="sm"
                      color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"}
                      fontWeight="500"
                      m={0}
                      noOfLines={1}
                    >
                      {analyst.name}
                    </Text>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>

        {/* Job */}
        <Box flex={1}>
          <Flex align="center" gap={2} mb={2}>
            <Icon as={AppIcons.workflow} boxSize={5} color="purple.700" />
            <Text fontSize="md" fontWeight="600" color="gray.600" m={0}>
              Workflow
            </Text>
          </Flex>
          {isLoadingJobs ? (
            <Flex align="center" gap={2} h="32px" borderRadius="full" bg="purple.50" px={3}>
              <Spinner size="sm" color="purple.600" thickness="2px" />
              <Text fontSize="sm" color="gray.400" m={0}>
                Loading...
              </Text>
            </Flex>
          ) : (
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                colorScheme="purple"
                bg="purple.50"
                borderColor="purple.50"
                _hover={{ bg: "purple.100", borderColor: "purple.100" }}
                borderRadius="lg"
                borderWidth="1px"
                px={3}
                py={1}
                w="100%"
                justifyContent="flex-start"
                isDisabled={scheduledJobs.length === 0}
              >
                <HStack spacing={2} justify="space-between" w="100%">
                  <Text fontSize="sm" color="fg.default" fontWeight="600" m={0} noOfLines={1}>
                    {selectedJob ? selectedJob.name : "Select workflow"}
                  </Text>
                  <Icon as={AppIcons.chevronDown} boxSize={3} />
                </HStack>
              </MenuButton>
              <MenuList
                maxH="250px"
                overflowY="auto"
                minW="200px"
                borderColor="purple.200"
                boxShadow="lg"
                zIndex={1500}
              >
                <MenuItem
                  fontSize="sm"
                  bg={!selectedJob ? "purple.700" : "transparent"}
                  _hover={{ bg: !selectedJob ? "purple.600" : "purple.50" }}
                  onClick={() => setSelectedJob(null)}
                >
                  <Text fontSize="sm" color={!selectedJob ? "white" : "fg.default"} fontWeight="500" m={0}>
                    Select workflow
                  </Text>
                </MenuItem>
                {scheduledJobs.length > 0 && <MenuDivider />}
                {scheduledJobs.map(job => (
                  <MenuItem
                    key={job.id}
                    fontSize="sm"
                    bg={selectedJob?.id === job.id ? "purple.700" : "transparent"}
                    _hover={{ bg: selectedJob?.id === job.id ? "purple.600" : "purple.50" }}
                    onClick={() => setSelectedJob(job)}
                  >
                    <HStack spacing={2}>
                      <Icon
                        as={AppIcons.workflow}
                        boxSize={5}
                        color={selectedJob?.id === job.id ? "white" : "purple.700"}
                      />
                      <Text
                        fontSize="sm"
                        color={selectedJob?.id === job.id ? "white" : "fg.default"}
                        fontWeight="500"
                        m={0}
                        noOfLines={1}
                      >
                        {job.name}
                      </Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          )}
          {!isLoadingJobs && scheduledJobs.length === 0 && selectedAnalyst && (
            <Text fontSize="xs" color="gray.400" mt={1} ml={1}>
              No workflow found for this analyst.
            </Text>
          )}
        </Box>
      </Flex>

      {/* Outputs list */}
      {selectedJob && (
        <Box>
          <Flex align="center" justify="space-between" mb={3} px={1} gap={3} flexWrap="wrap">
            <Text fontSize="sm" color="gray.500" fontWeight="600" m={0}>
              {isLoadingOutputs
                ? "Loading outputs..."
                : outputs.length > 0
                  ? `Showing ${outputs.length} of ${totalOutputs} output${totalOutputs !== 1 ? "s" : ""}`
                  : "No outputs found"}
            </Text>
            {!isLoadingOutputs && outputs.length > 0 && (
              <ButtonGroup size="sm" isAttached variant="outline" colorScheme="purple" flexShrink={0}>
                <Button
                  variant={outputsViewMode === "runsOnly" ? "solid" : "outline"}
                  colorScheme="purple"
                  onClick={() => setOutputsViewMode("runsOnly")}
                  aria-pressed={outputsViewMode === "runsOnly"}
                >
                  <HStack spacing={1.5}>
                    <Icon as={AppIcons.list} boxSize={3.5} />
                    <Text as="span" m={0} mb={0}>
                      List
                    </Text>
                  </HStack>
                </Button>
                <Button
                  variant={outputsViewMode === "withFindings" ? "solid" : "outline"}
                  colorScheme="purple"
                  onClick={() => setOutputsViewMode("withFindings")}
                  aria-pressed={outputsViewMode === "withFindings"}
                >
                  <HStack spacing={1.5}>
                    <Icon as={AppIcons.findings} boxSize={4} />
                    <Text as="span" m={0} mb={0}>
                      With Findings
                    </Text>
                  </HStack>
                </Button>
              </ButtonGroup>
            )}
          </Flex>

          {isLoadingOutputs ? (
            <Flex justify="center" align="center" h="120px">
              <Spinner size="md" color="purple.700" thickness="2px" />
            </Flex>
          ) : outputs.length === 0 ? (
            <Alert status="info" bg="gray.50" border="1px" borderColor="purple.600" color="purple.700" fontWeight="600">
              <AlertIcon color="purple.700" />
              No workflow outputs found for this job.
            </Alert>
          ) : outputsViewMode === "withFindings" ? (
            <Box
              overflowY="auto"
              overflowX="auto"
              maxH="calc(100vh - 360px)"
              pr={1}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
              css={{
                "&::-webkit-scrollbar": { width: "4px", height: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "#dfc2f0", borderRadius: "2px" },
              }}
            >
              <Table variant="simple" size="md" layout="fixed" w="100%">
                <Thead
                  bg="gray.50"
                  position="sticky"
                  top={0}
                  zIndex={1}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Tr>
                    <Th
                      w="30%"
                      py={3}
                      verticalAlign="middle"
                      color="gray.700"
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      borderColor="gray.200"
                    >
                      Run
                    </Th>
                    <Th
                      py={3}
                      verticalAlign="middle"
                      minW={0}
                      color="gray.700"
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      borderColor="gray.200"
                    >
                      Findings
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {outputs.map((output, idx) => {
                    const rowKey = output.analysis_output?.header?.id ?? idx;
                    const title = analysisRunTitle(output?.analysis_output ?? {}, defaultTimeZone);
                    const findings = output?.analysis_output ? output?.analysis_output?.findings?.trim() || "" : "";
                    const chatId = output?.chat_id?.trim();
                    return (
                      <Tr key={rowKey} verticalAlign="top" _hover={{ bg: "purple.50" }}>
                        <Td borderColor="gray.100" py={4} verticalAlign="middle" w="30%">
                          <Flex align="center" gap={2} minH="100%">
                            <Icon as={AppIcons.workflow} boxSize={4} color="purple.600" flexShrink={0} />
                            <Text
                              as="span"
                              display="inline-block"
                              maxW="100%"
                              fontSize="sm"
                              fontWeight="600"
                              color={chatId ? "purple.700" : "gray.800"}
                              m={0}
                              mb={0}
                              lineHeight="short"
                              cursor={chatId ? "pointer" : "default"}
                              _hover={chatId ? { textDecoration: "underline", color: "purple.800" } : undefined}
                              onClick={chatId ? () => window.open(`/chat/${chatId}`, "_blank") : undefined}
                            >
                              {title}
                            </Text>
                          </Flex>
                        </Td>
                        <Td borderColor="gray.100" py={4} verticalAlign="top" minW={0}>
                          <Box
                            minW={0}
                            w="100%"
                            maxW="100%"
                            fontSize="md"
                            lineHeight="1.5"
                            maxH="320px"
                            overflowY="auto"
                            overflowX="hidden"
                            pr={1}
                            whiteSpace="normal"
                            wordBreak="break-word"
                            css={{
                              WebkitOverflowScrolling: "touch",
                              "&::-webkit-scrollbar": { width: "6px" },
                              "&::-webkit-scrollbar-thumb": { background: "#dfc2f0", borderRadius: "3px" },
                            }}
                            sx={{
                              "& p:first-of-type": { mt: 0 },
                              "& p:last-child": { mb: 0 },
                              "& .markdown-content": { minW: 0, maxW: "100%" },
                            }}
                          >
                            {findings ? (
                              <MarkdownRenderer content={findings} />
                            ) : (
                              <Text fontSize="sm" color="gray.400" fontStyle="italic" m={0} mb={0}>
                                No findings recorded.
                              </Text>
                            )}
                          </Box>
                        </Td>
                      </Tr>
                    );
                  })}
                  {outputs.length < totalOutputs && (
                    <Tr>
                      <Td colSpan={2} borderColor="gray.100" py={3} textAlign="center">
                        <Button
                          variant="outline"
                          colorScheme="purple"
                          size="sm"
                          onClick={handleLoadMore}
                          isLoading={isLoadingMore}
                          loadingText="Loading more..."
                          w="100%"
                        >
                          Load More
                        </Button>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Box
              overflowY="auto"
              maxH="calc(100vh - 360px)"
              pr={1}
              css={{
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "#dfc2f0", borderRadius: "2px" },
              }}
            >
              <VStack spacing={2} align="stretch">
                {outputs.map((output, idx) => (
                  <RunListRow
                    key={output?.analysis_output?.header?.id ?? idx}
                    output={output?.analysis_output ?? {}}
                    title={analysisRunTitle(output?.analysis_output ?? {}, defaultTimeZone)}
                    chatId={output?.chat_id?.trim()}
                  />
                ))}
                {outputs.length < totalOutputs && (
                  <Button
                    variant="outline"
                    colorScheme="purple"
                    size="sm"
                    onClick={handleLoadMore}
                    isLoading={isLoadingMore}
                    loadingText="Loading more..."
                    w="100%"
                  >
                    Load More
                  </Button>
                )}
              </VStack>
            </Box>
          )}
        </Box>
      )}
    </VStack>
  );
};

export default AnalysisRun;
